import { v4 as uuidv4 } from 'uuid';
import { stripUndefined } from './appsyncPublicGraphql';
import { communityGraphqlRequest } from './communityGraphql';
import { emitNotificationsChanged, isMessageNotificationForOpenChat } from './openMessageChat';

/** Matches tinywins-community Notification.notificationType enum. */
export type NotificationType =
  | 'ASSESSMENT_ASSIGNED'
  | 'ASSESSMENT_COMPLETED'
  | 'MESSAGE_RECEIVED'
  | 'THERAPIST_REFERRAL'
  | 'REFERRAL_DECIDED'
  | 'GOAL_CREATED'
  | 'GOAL_COMPLETED'
  | 'SESSION_COMPLETED'
  | 'CARE_PLAN_CREATED'
  | 'PATIENT_ASSIGNED'
  | 'PATIENT_REMOVED'
  | 'ORG_INVITE_SENT'
  | 'ORG_INVITE_ACCEPTED'
  | 'ORG_MEMBER_JOINED'
  | 'APPOINTMENT_REQUESTED'
  | 'APPOINTMENT_UPDATED'
  | 'SYSTEM_ALERT';

export type AppNotification = {
  id: string;
  fromUserId?: string | null;
  toUserId: string;
  organizationId?: string | null;
  kidProfileId?: string | null;
  notificationType: NotificationType;
  title: string;
  body?: string | null;
  payload?: unknown;
  readAt?: string | null;
  actionUrl?: string | null;
  createdAt: string;
};

const NOTIFICATION_SELECTION = `
  id fromUserId toUserId organizationId kidProfileId notificationType title body payload readAt actionUrl createdAt
`;

function parsePayload(raw: unknown): unknown {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as unknown;
    } catch {
      return raw;
    }
  }
  return raw;
}

function mapNotification(row: Partial<AppNotification> | null | undefined): AppNotification | null {
  if (!row?.id || !row.toUserId || !row.title || !row.createdAt || !row.notificationType) return null;
  return {
    id: row.id,
    fromUserId: row.fromUserId ?? null,
    toUserId: row.toUserId,
    organizationId: row.organizationId ?? null,
    kidProfileId: row.kidProfileId ?? null,
    notificationType: row.notificationType as NotificationType,
    title: row.title,
    body: row.body ?? null,
    payload: parsePayload(row.payload),
    readAt: row.readAt ?? null,
    actionUrl: row.actionUrl ?? null,
    createdAt: row.createdAt,
  };
}

function explainMissing(e: unknown): Error {
  const msg = e instanceof Error ? e.message : String(e);
  if (/Unknown type|Notification|Cannot query field|not present|notificationsByToUserId/i.test(msg)) {
    return new Error(
      'Notification model is not available on the community API yet. Deploy tinywins-community (Notification + toUserId index) and set VITE_COMMUNITY_GRAPHQL_URL.',
    );
  }
  return e instanceof Error ? e : new Error(msg);
}

async function createNotificationGraphql(input: Record<string, unknown>): Promise<AppNotification> {
  const data = await communityGraphqlRequest<{
    createNotification?: Partial<AppNotification> | null;
  }>(
    `mutation CreateNotification($input: CreateNotificationInput!) {
      createNotification(input: $input) { ${NOTIFICATION_SELECTION} }
    }`,
    { input: stripUndefined(input) },
  );
  const mapped = mapNotification(data.createNotification);
  if (!mapped) throw new Error('Notification was not created.');
  return mapped;
}

type NotificationPage = {
  items?: Array<Partial<AppNotification> | null> | null;
  nextToken?: string | null;
};

async function listNotificationsByToUserIdGraphql(toUserId: string): Promise<AppNotification[]> {
  const out: AppNotification[] = [];
  let nextToken: string | null = null;
  do {
    const data = await communityGraphqlRequest<{
      notificationsByToUserId?: NotificationPage | null;
    }>(
      `query NotificationsByToUserId($toUserId: ID!, $sortDirection: ModelSortDirection, $limit: Int, $nextToken: String) {
        notificationsByToUserId(toUserId: $toUserId, sortDirection: $sortDirection, limit: $limit, nextToken: $nextToken) {
          items { ${NOTIFICATION_SELECTION} }
          nextToken
        }
      }`,
      { toUserId, sortDirection: 'DESC', limit: 100, nextToken },
    );
    for (const item of data.notificationsByToUserId?.items || []) {
      const mapped = mapNotification(item);
      if (mapped) out.push(mapped);
    }
    nextToken = data.notificationsByToUserId?.nextToken ?? null;
  } while (nextToken);
  return out;
}

/** Fallback if the GSI query is not deployed yet. Paginates so AppSync limit-before-filter cannot drop rows. */
async function listNotificationsScanGraphql(toUserId: string): Promise<AppNotification[]> {
  const out: AppNotification[] = [];
  let nextToken: string | null = null;
  do {
    const data = await communityGraphqlRequest<{
      listNotifications?: NotificationPage | null;
    }>(
      `query ListNotifications($limit: Int, $nextToken: String) {
        listNotifications(limit: $limit, nextToken: $nextToken) {
          items { ${NOTIFICATION_SELECTION} }
          nextToken
        }
      }`,
      { limit: 200, nextToken },
    );
    for (const item of data.listNotifications?.items || []) {
      const mapped = mapNotification(item);
      if (mapped && mapped.toUserId === toUserId) out.push(mapped);
    }
    nextToken = data.listNotifications?.nextToken ?? null;
  } while (nextToken);
  return out;
}

async function listNotificationsGraphql(toUserId: string): Promise<AppNotification[]> {
  try {
    return await listNotificationsByToUserIdGraphql(toUserId);
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    if (!/Cannot query field|notificationsByToUserId|Unknown type/i.test(msg)) throw e;
    return listNotificationsScanGraphql(toUserId);
  }
}

async function updateNotificationGraphql(id: string, readAt: string): Promise<void> {
  await communityGraphqlRequest(
    `mutation UpdateNotification($input: UpdateNotificationInput!) {
      updateNotification(input: $input) { id readAt }
    }`,
    { input: { id, readAt } },
  );
}

async function deleteNotificationGraphql(id: string): Promise<void> {
  await communityGraphqlRequest(
    `mutation DeleteNotification($input: DeleteNotificationInput!) {
      deleteNotification(input: $input) { id }
    }`,
    { input: { id } },
  );
}

function payloadKey(
  payload: Record<string, unknown> | undefined,
): { key: string; value: string } | null {
  if (!payload) return null;
  const keys = [
    'assignmentId',
    'sessionGroupId',
    'carePlanId',
    'milestoneTaskId',
    'referralId',
  ] as const;
  for (const key of keys) {
    const value = payload[key];
    if (typeof value === 'string' && value) return { key, value };
  }
  return null;
}

export async function createNotification(params: {
  toUserId: string;
  fromUserId?: string | null;
  organizationId?: string | null;
  kidProfileId?: string | null;
  notificationType: NotificationType;
  title: string;
  body?: string;
  payload?: Record<string, unknown>;
  actionUrl?: string;
}): Promise<void> {
  if (!params.toUserId) return;
  if (params.fromUserId && params.fromUserId === params.toUserId) return;

  const dedupe = payloadKey(params.payload);
  if (dedupe) {
    const existing = await listNotificationsForUser(params.toUserId, { unreadOnly: true, limit: 100 });
    const dup = existing.find((n) => {
      if (n.notificationType !== params.notificationType) return false;
      const p = n.payload as Record<string, unknown> | undefined;
      return p?.[dedupe.key] === dedupe.value;
    });
    if (dup) return;
  }

  try {
    const payload =
      params.payload === undefined || params.payload === null
        ? undefined
        : JSON.stringify(params.payload);

    await createNotificationGraphql({
      id: uuidv4(),
      toUserId: params.toUserId,
      fromUserId: params.fromUserId ?? undefined,
      organizationId: params.organizationId ?? undefined,
      kidProfileId: params.kidProfileId ?? undefined,
      notificationType: params.notificationType,
      title: params.title,
      body: params.body,
      payload,
      actionUrl: params.actionUrl,
      createdAt: new Date().toISOString(),
    });
    emitNotificationsChanged();
  } catch (e) {
    throw explainMissing(e);
  }
}

export async function listNotificationsForUser(
  userId: string,
  opts?: { unreadOnly?: boolean; limit?: number },
): Promise<AppNotification[]> {
  const max = opts?.limit ?? 50;
  try {
    const rows = await listNotificationsGraphql(userId);
    return rows
      .filter((n) => (opts?.unreadOnly ? !n.readAt : true))
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
      .slice(0, max);
  } catch (e) {
    console.warn('[notifications] list failed:', explainMissing(e).message);
    return [];
  }
}

export async function countUnreadNotifications(userId: string): Promise<number> {
  const rows = await listNotificationsForUser(userId, { unreadOnly: true, limit: 200 });
  return rows.length;
}

export async function countUnreadByKidProfile(
  userId: string,
  kidProfileIds: string[],
): Promise<Record<string, number>> {
  const rows = await listNotificationsForUser(userId, { unreadOnly: true, limit: 200 });
  const counts: Record<string, number> = {};
  for (const id of kidProfileIds) counts[id] = 0;
  for (const n of rows) {
    if (n.kidProfileId && kidProfileIds.includes(n.kidProfileId)) {
      counts[n.kidProfileId] = (counts[n.kidProfileId] || 0) + 1;
    }
  }
  return counts;
}

export async function markNotificationRead(notificationId: string): Promise<void> {
  try {
    await updateNotificationGraphql(notificationId, new Date().toISOString());
    emitNotificationsChanged();
  } catch (e) {
    throw explainMissing(e);
  }
}

/** Remove a notification from the bell (delete). Used after click / open. */
export async function dismissNotification(notificationId: string): Promise<void> {
  try {
    await deleteNotificationGraphql(notificationId);
    emitNotificationsChanged();
  } catch (e) {
    try {
      await updateNotificationGraphql(notificationId, new Date().toISOString());
      emitNotificationsChanged();
    } catch (e2) {
      throw explainMissing(e2);
    }
  }
}

export async function markNotificationsReadForKid(userId: string, kidProfileId: string): Promise<void> {
  const rows = await listNotificationsForUser(userId, { unreadOnly: true, limit: 200 });
  const forKid = rows.filter((n) => n.kidProfileId === kidProfileId);
  await Promise.all(forKid.map((n) => dismissNotification(n.id)));
}

/** Remove chat notifications for an open peer thread (click/open clears the bell). */
export async function markMessageNotificationsReadForChat(
  userId: string,
  opts: { peerUserId: string; kidProfileId: string },
): Promise<number> {
  const rows = await listNotificationsForUser(userId, { limit: 100 });
  const matches = rows.filter((n) => {
    if (n.notificationType !== 'MESSAGE_RECEIVED') return false;
    const payload = n.payload as {
      kind?: string;
      fromUserId?: string | null;
      kidProfileId?: string | null;
      orgTeam?: boolean;
    } | null;
    const fromId = payload?.fromUserId || n.fromUserId || null;
    const kidId = payload?.kidProfileId || n.kidProfileId || null;
    if (fromId !== opts.peerUserId) return false;
    if (opts.kidProfileId === 'org-team') {
      return !kidId || kidId === 'org-team' || payload?.orgTeam === true;
    }
    return kidId === opts.kidProfileId;
  });
  if (matches.length === 0) return 0;
  await Promise.all(matches.map((n) => deleteNotificationGraphql(n.id).catch(() => undefined)));
  emitNotificationsChanged();
  return matches.length;
}

export { isMessageNotificationForOpenChat };
